"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Pending", to: "Approved, Rejected" },
  { from: "Approved, Rejected", to: "Cancelled" },
  { from: "Cancelled", to: "Nothing. Cancelled is a dead end" },
];

const APPLY_FROM = [
  {
    from: "Bill",
    when: "Open, Partially Paid, or Overdue, with a balance greater than zero, same vendor and branch",
    result:
      "You pick the bill on Add Payment Made and enter how much of this payment to apply. Approving moves the bill to Partially Paid or Paid",
  },
  {
    from: "Vendor opening balance",
    when: "That vendor still has remaining opening payable on this branch (positive Opening Balance)",
    result:
      "The allocation table includes a line labelled (Vendor opening balance). Approve reduces that remaining payable. Only opening payable can be applied here — vendor credit is Apply Credit on Bills",
  },
  {
    from: "Scratch / advance",
    when: "Advanced Payment is ticked, or you pay more than you allocate",
    result:
      "The leftover sits as Unused Amount. After Approve you can Apply to Bill, Auto Pay Bills, or Refund it",
  },
];

const AFTER_APPROVE = [
  {
    to: "Bill / opening payable",
    when: "Approved and Unused Amount is greater than zero",
    where: "Apply to Bill on the list or detail, or Auto Pay Bills on the list",
    result:
      "Uses unused money against open bills or remaining opening payable. Does not create a second payment",
  },
  {
    to: "Refund",
    when: "Approved and Unused Amount is greater than zero",
    where: "Refund on the list or detail",
    result: "Returns unused money. Unused Amount goes down",
  },
];

const RULES = [
  {
    topic: "Ledger posts on Approve, not on Save",
    rule: "Save creates a Pending payment. Nothing hits Paid Through, Accounts Payable, or vendor Current Balance until an Accountant Approves. Cancel or delete of an Approved payment reverses those journals. Journal posting on Approve runs when the payment is in company base currency",
  },
  {
    topic: "Subscription gate",
    rule: "Only creating a payment is limited by your plan. Editing, approve, apply, refund, and deleting are not separately gated",
  },
  {
    topic: "No primary bank requirement",
    rule: "You must pick a Paid Through account (Cash, Bank, Payment Clearing, or Credit Card on the chart of accounts). You do not need Make this primary on a bank",
  },
  {
    topic: "Payment number",
    rule: "Auto-generated with a PMTM prefix unless you turn that off under Configure Payment Made Number on the form. Locked after save",
  },
  {
    topic: "Division, Branch, Warehouse, Vendor, Paid Through",
    rule: "All required to create. Vendor and location stay locked after save. You can only edit a Pending payment",
  },
  {
    topic: "Currency",
    rule: "Taken from the bills or opening-payable line you apply to, or from the vendor on an advance. Cross-currency payments are blocked. An exchange-rate field appears when the document currency differs from the company base",
  },
  {
    topic: "Payment modes",
    rule: "Petty Cash, Cheque, Bank Transfer, or UPI. Cheque has Cheque No and PDC Cheque Date. Unlike Payments Received, Approve sets the cheque Cleared and the payment Approved in one step — there is no receive-then-deposit walk",
  },
  {
    topic: "Approve and Reject are Accountant-only",
    rule: "Offered to an Accountant with Edit on a Pending payment. Rejected payments can still be Cancelled. There is no Draft status",
  },
  {
    topic: "Unused Amount",
    rule: "Paid Amount minus what you allocated on this payment. After Approve you can Apply to Bill (including remaining opening payable) or Refund. It is not Unused Credit on the vendor drawer (that is Debit Notes)",
  },
  {
    topic: "Advance already applied cannot cancel or delete",
    rule: "Unapply the bill or opening-payable line first. Then cancel or delete is allowed",
  },
  {
    topic: "No Duplicate, no list Export",
    rule: "Detail has Download (PDF) and Print only",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each. Also offered on the Approve modal",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a payment made" },
      { heading: "2. Against bills, advance, and unused amount" },
      { heading: "3. Paying against a vendor opening balance" },
      { heading: "4. Viewing, searching, and the list" },
      { heading: "5. Editing a payment" },
      { heading: "6. Changing status — including cheque" },
      { heading: "7. After Approve — apply unused, auto pay, refund" },
      { heading: "8. Cancelling and deleting" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function PaymentsMadePage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Payments Made
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone paying a vendor — against bills, leftover unused
            amount, or opening payable
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Payment Made</B> is money out. <B>Save</B> always creates{" "}
            <B>Pending</B> — the bank/cash account, payables, and Current
            Balance wait for an Accountant to <B>Approve</B>. There is no
            Draft. Sign of opening balance is explained on Vendors; here
            you only allocate <B>(Vendor opening balance)</B> when it is
            opening payable.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a payment made
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick Division, Branch, Warehouse, then Vendor. There is no Save
            &amp; Approve.
          </p>
          <Definitions
            items={[
              {
                term: "Payment#",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Configure Payment Made Number</B> (prefix, next
                    number). Default prefix <B>PMTM</B>. Locked after save.
                  </>
                ),
              },
              {
                term: "Paid Through",
                desc: (
                  <>
                    Required. Chart of accounts categories Cash, Bank,
                    Payment Clearing, or Credit Card.
                  </>
                ),
              },
              {
                term: "Payment Mode",
                desc: (
                  <>
                    Petty Cash, Cheque, Bank Transfer, or UPI. Cheque adds
                    Cheque No and PDC Cheque Date.
                  </>
                ),
              },
              {
                term: "Paid Amount",
                desc: <>Must be greater than zero.</>,
              },
              {
                term: "Should this amount be reflected in the payment field?",
                desc: (
                  <>
                    When ticked, line Payment amounts fill from Paid Amount.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            Unlike a Bill, a payment does <B>not</B> post to the ledger when
            you save. Posting waits for Approve.
          </Callout>
          <Screenshot caption="Screenshot: Add Payment Made with Vendor, Paid Through, Payment Mode, Paid Amount, and Save." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Against bills, advance, and unused amount
          </h3>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {APPLY_FROM.map((r, i) => (
                  <tr
                    key={r.from}
                    className={
                      i < APPLY_FROM.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                      {r.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Summary strip: Allocated Amount, Total Amount Paid, Unused
            Amount. Type badge <B>Advance</B> vs <B>Bill</B>.
          </p>
          <Screenshot caption="Screenshot: bill allocation table with (Vendor opening balance) and Unused Amount." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Paying against a vendor opening balance
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only remaining <B>opening payable</B> (positive vendor Opening
            Balance on this branch). The API refuses vendor-credit OB on
            this module. Apply remaining vendor credit on Bills instead.
          </p>
          <Callout type="note">
            Full they-owe-you vs you-owe-them sign rules live on Vendors.
            This screen only shows the allocation line.
          </Callout>
          <Screenshot caption="Screenshot: (Vendor opening balance) line under Bill Id on Add Payment Made." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Viewing, searching, and the list
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Status tabs Pending / Approved / Rejected / Cancelled.{" "}
            <B>Auto Pay Bills</B> when pending mapping exists. No Excel
            export, no Duplicate. Row More: Approve, Reject, Cancel
            Payment, Refund, Apply to Bill. Detail: View Vendor, Download,
            Print, refund history, Approved By.
          </p>
          <Screenshot caption="Screenshot: Payments Made list with Auto Pay Bills and status tabs." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Editing a payment
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Pending only. Vendor and location stay locked.
          </p>

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing status — including cheque
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Accountant + Edit. Modal title <B>Approve Payment</B>. For
            cheque, the button can read Receive Cheque Approval or Deposit
            Cheque from stored cheque status, but Approve still sets cheque{" "}
            <B>Cleared</B> and payment <B>Approved</B> immediately — not
            the three-step path on Payments Received.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Then
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[200px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Approve Payment on a Pending payment made." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. After Approve — apply unused, auto pay, refund
          </h3>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    To
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Where
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {AFTER_APPROVE.map((r, i) => (
                  <tr
                    key={r.to}
                    className={
                      i < AFTER_APPROVE.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                      {r.to}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.where}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Auto Pay Bills modal: <B>Auto Adjust Advance Payments to Bills</B>
            . Order: opening payable on the branch, then unused advances,
            then open bills by due date (oldest first).
          </p>
          <Screenshot caption="Screenshot: Apply to Bill and Refund on an Approved payment with unused amount." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Cancelling and deleting
          </h3>
          <Definitions
            items={[
              {
                term: "Cancel Payment",
                desc: (
                  <>
                    Approved or Rejected, Accountant + Edit. Blocked if this
                    advance still has bills or opening-payable lines applied.
                    If it was Approved, bill remaining amounts, OB remaining,
                    Current Balance, and journals reverse.
                  </>
                ),
              },
              {
                term: "Delete",
                desc: (
                  <>
                    Pending or Approved in the UI, same applied-advance
                    block. Same reversals if it had been Approved, then the
                    record is gone. Rejected and Cancelled are not deletable
                    in the UI.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Cancel blocked because the advance was already applied to a bill." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/purchase/bills"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Bills <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Open, Partially Paid, and Overdue bills this payment can settle
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/vendors"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Vendors <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Positive Opening Balance is what (Vendor opening balance)
              collects
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/expenses"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Expenses <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Direct spend that does not go through a bill
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
